/**
 * Kentucky State-Specific Diagnostic Questions
 * All questions reference actual KY regulations, forms, and requirements
 * Regulatory Body: Cabinet for Health and Family Services (CHFS) / Office of Inspector General (OIG)
 * Primary Code: 902 KAR 20:081 (Operations and Services; Home Health Agencies)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const KENTUCKY_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'KY-A1',
    itemId: 'A1',
    stateCode: 'KY',
    title: 'CHFS Home Health Agency License Status',
    description: 'Kentucky requires all home health agencies to be licensed by CHFS under 902 KAR 20:081',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Licensure',
    agencyName: 'Cabinet for Health and Family Services - Office of Inspector General',
    agencyAbbr: 'CHFS/OIG',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov/agencies/os/oig/dhc/Pages/hha.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from CHFS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health licenses must be renewed annually with CHFS/OIG',
      },
      {
        id: 'q2',
        question: 'What is your CHFS home health agency license expiration date?',
        inputType: 'date',
        helpText: 'Found on your license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your CHFS Home Health Agency license is current and active per 902 KAR 20:081.',
      atRisk: 'Your license expires within 90 days. Kentucky requires renewal applications submitted 45 days before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Kentucky without a valid CHFS license violates 902 KAR 20:081 and can result in civil penalties. Contact CHFS/OIG immediately at (502) 564-7963.',
      unknown: 'Verify your license status with CHFS/OIG at (502) 564-7963.',
    },
    stateNotes: [
      'Home health licenses must be renewed annually',
      'Renewal applications must be submitted 45 days before expiration',
      'Administrator must meet 902 KAR 20:081 qualifications',
      'Agency must maintain a Kentucky physical location',
      'Medicare/Medicaid certification requires additional requirements',
    ],
  },

  {
    id: 'KY-A2',
    itemId: 'A2',
    stateCode: 'KY',
    title: 'Administrator Qualifications (902 KAR 20:081)',
    description: 'Kentucky requires a qualified Administrator/Director',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Administrator',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov/agencies/os/oig/dhc/Pages/hha.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Does your Administrator meet Kentucky qualification requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'licensed', label: 'Licensed RN or healthcare professional' },
          { value: 'experience', label: 'At least 1 year supervisory experience in healthcare' },
          { value: 'fulltime', label: 'Available full-time for agency operations' },
          { value: 'documented', label: 'Qualifications documented in personnel file' },
          { value: 'designated', label: 'Designated administrator on file with CHFS' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet Kentucky 902 KAR 20:081 requirements.',
      atRisk: 'Administrator qualifications may not be fully documented. Verify all requirements are met and on file.',
      nonCompliant: 'Administrator does not meet 902 KAR 20:081 requirements. Contact CHFS at (502) 564-7963 for guidance.',
      unknown: 'Verify Administrator qualifications against 902 KAR 20:081 requirements.',
    },
    stateNotes: [
      'Administrator must be licensed RN or healthcare professional per 902 KAR 20:081',
      'Minimum 1 year supervisory experience in healthcare required',
      'Must be available full-time for agency operations',
      'Administrator designation must be on file with CHFS',
    ],
  },

  {
    id: 'KY-A3',
    itemId: 'A3',
    stateCode: 'KY',
    title: 'Insurance Requirements (902 KAR 20:081)',
    description: 'Kentucky requires specific insurance coverage for home health agencies',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Insurance',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'What is your general liability insurance per-occurrence coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Kentucky recommends minimum $1,000,000 per occurrence',
      },
      {
        id: 'q2',
        question: 'What is your general liability insurance aggregate coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Kentucky recommends minimum $3,000,000 aggregate',
      },
      {
        id: 'q3',
        question: 'Do you maintain professional liability/malpractice insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q4',
        question: 'Do you maintain workers\' compensation insurance covering all employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Workers\' compensation required per Kentucky law',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 3000000 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 500000 },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'lessThan', questionId: 'q1', value: 500000 },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets Kentucky 902 KAR 20:081 requirements.',
      atRisk: 'Your insurance may not meet all Kentucky requirements. Review coverage levels with CHFS.',
      nonCompliant: 'Your insurance coverage does not meet Kentucky requirements. Missing workers\' compensation or inadequate liability coverage creates significant risk.',
      unknown: 'Verify your insurance meets 902 KAR 20:081 requirements.',
    },
    stateNotes: [
      'General liability: $1M per occurrence, $3M aggregate recommended',
      'Workers\' compensation required by Kentucky law',
      'Professional liability required for skilled nursing services',
      'Proof of insurance required with license application and renewal',
    ],
  },

  {
    id: 'KY-A4',
    itemId: 'A4',
    stateCode: 'KY',
    title: 'Written Policies and Procedures (902 KAR 20:081)',
    description: 'Kentucky requires specific written policies per 902 KAR 20:081',
    severity: 'medium',
    regulationCode: '902-20-081',
    regulationName: 'Policies',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Kentucky-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient Bill of Rights' },
          { value: 'admission', label: 'Admission and discharge criteria' },
          { value: 'personnel', label: 'Personnel policies and job descriptions' },
          { value: 'supervision', label: 'Clinical supervision procedures' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Background check procedures (KARES/KRS 216.789)' },
          { value: 'infection_control', label: 'Infection control program' },
          { value: 'emergency', label: 'Emergency preparedness plan' },
          { value: 'quality', label: 'Quality assurance program' },
          { value: 'records', label: 'Record retention policy (5 years per 907 KAR 1:030)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Kentucky 902 KAR 20:081 requirements.',
      atRisk: 'Your policy manual is missing required sections per 902 KAR 20:081. CHFS surveyors will review your policies for completeness. Missing: {missing}.',
      nonCompliant: 'Multiple required policies are missing per 902 KAR 20:081. This is a common CHFS survey deficiency. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against 902 KAR 20:081 requirements.',
    },
    stateNotes: [
      'Written policies required per 902 KAR 20:081 Section 4',
      'Must include Patient Bill of Rights per Kentucky law',
      'Background check procedures must reference KARES/KRS 216.789',
      'Record retention must meet 5-year requirement per 907 KAR 1:030',
    ],
  },

  {
    id: 'KY-A5',
    itemId: 'A5',
    stateCode: 'KY',
    title: 'Incident and Abuse Reporting (KRS 209.030)',
    description: 'Kentucky requires documented incident and mandatory abuse reporting procedures',
    severity: 'high',
    regulationCode: '209.030',
    regulationName: 'Abuse Reporting',
    agencyName: 'Kentucky Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(800) 752-6200',
    agencyUrl: 'https://chfs.ky.gov/agencies/dcbs/dpp/apb/Pages/default.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident and abuse reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented with APS hotline information available' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
        helpText: 'KRS 209.030 requires reporting of suspected adult abuse/neglect',
      },
      {
        id: 'q2',
        question: 'Which types of incidents does your reporting process cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical abuse' },
          { value: 'neglect', label: 'Neglect or self-neglect' },
          { value: 'financial', label: 'Financial exploitation' },
          { value: 'emotional', label: 'Psychological/emotional abuse' },
          { value: 'sexual', label: 'Sexual abuse' },
          { value: 'injury', label: 'Patient injuries during service' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident and abuse reporting process is documented per KRS 209.030.',
      atRisk: 'Incident reporting process exists but may not be fully documented. Ensure APS hotline (800) 752-6200 or (877) 597-2331 is available to all staff.',
      nonCompliant: 'No incident reporting process is in place. KRS 209.030 requires reporting of suspected abuse. All staff are mandated reporters.',
      unknown: 'Verify your incident reporting procedures meet KRS 209.030 requirements.',
    },
    stateNotes: [
      'Kentucky Adult Protective Services Hotline: (800) 752-6200 or (877) 597-2331 (KYSAFE1)',
      'Reports can be made 24/7',
      'All home health workers are mandated reporters under KRS 209.030',
      'Online reporting available for non-emergency situations',
      'CHFS investigates all reports of suspected abuse/neglect',
    ],
  },

  {
    id: 'KY-A6',
    itemId: 'A6',
    stateCode: 'KY',
    title: 'Emergency Preparedness Plan (902 KAR 20:081)',
    description: 'Kentucky requires documented emergency preparedness procedures',
    severity: 'medium',
    regulationCode: '902-20-081',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan addressing Kentucky hazards' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your emergency plan address the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'severe_weather', label: 'Severe weather (tornadoes, flooding, ice storms)' },
          { value: 'patient_contact', label: 'Patient emergency contact procedures' },
          { value: 'staff_notification', label: 'Staff notification procedures' },
          { value: 'continuity', label: 'Service continuity plans' },
          { value: 'communication', label: 'Communication backup plans' },
          { value: 'evacuation', label: 'Patient evacuation assistance' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and addresses Kentucky-specific hazards per 902 KAR 20:081.',
      atRisk: 'Emergency preparedness plan is incomplete. Include procedures for severe weather, flooding, and service continuity.',
      nonCompliant: 'No emergency preparedness plan is in place. This violates 902 KAR 20:081.',
      unknown: 'Review your emergency preparedness procedures for Kentucky-specific hazards.',
    },
    stateNotes: [
      'Kentucky experiences tornadoes, severe storms, and flooding',
      'Eastern Kentucky particularly prone to flash flooding',
      'Ice storms can disrupt services for extended periods',
      'Patient priority lists for emergency contact essential',
    ],
  },

  {
    id: 'KY-A7',
    itemId: 'A7',
    stateCode: 'KY',
    title: 'Quality Assurance Program (902 KAR 20:081)',
    description: 'Kentucky requires ongoing quality assessment and performance improvement',
    severity: 'medium',
    regulationCode: '902-20-081',
    regulationName: 'Quality Assurance',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct and document quality assurance reviews?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented reviews (at least quarterly)' },
          { value: 'occasional', label: 'Occasional reviews, not consistently documented' },
          { value: 'no', label: 'No quality assurance reviews' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program meets 902 KAR 20:081 requirements.',
      atRisk: 'Quality assurance reviews are not conducted consistently. Implement regular documented reviews.',
      nonCompliant: 'No quality assurance program exists. This violates 902 KAR 20:081.',
      unknown: 'Review quality assurance requirements under 902 KAR 20:081.',
    },
    stateNotes: [
      'Quality assurance program required per 902 KAR 20:081',
      'Regular documented reviews recommended at least quarterly',
      'Include patient outcome measures and staff performance',
      'Action plans required for identified deficiencies',
    ],
  },

  {
    id: 'KY-A8',
    itemId: 'A8',
    stateCode: 'KY',
    title: 'Record Retention (907 KAR 1:030)',
    description: 'Kentucky requires minimum 5-year record retention',
    severity: 'medium',
    regulationCode: '907-1-030',
    regulationName: 'Record Retention',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets KY requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Kentucky 907 KAR 1:030 requires retention for at least 5 years from date of service',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Kentucky\'s 5-year requirement under 907 KAR 1:030.',
      atRisk: 'Kentucky requires 5-year record retention per 907 KAR 1:030. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Kentucky\'s 5-year requirement. Immediately update your retention policy.',
      unknown: 'Review record retention requirements under 907 KAR 1:030.',
    },
    stateNotes: [
      'Kentucky requires 5-year retention from date of service',
      'Records for minors require longer retention',
      'Both patient and employee records subject to requirement',
      'Must retain until audit disputes resolved beyond 5 years',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'KY-B1',
    itemId: 'B1',
    stateCode: 'KY',
    title: 'Personnel File Requirements (902 KAR 20:081)',
    description: 'Kentucky requires specific documents in each staff member\'s personnel file',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Personnel',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY staff member\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'kares', label: 'KARES background check clearance' },
          { value: 'abuse_registry', label: 'Cabinet Abuse Registry check' },
          { value: 'tb', label: 'Annual TB screening documentation' },
          { value: 'licenses', label: 'Current professional licenses (KBN verification)' },
          { value: 'orientation', label: 'Orientation completion documentation' },
          { value: 'competency', label: 'Competency evaluation records' },
          { value: 'job_desc', label: 'Signed job description' },
          { value: 'dementia_training', label: 'Dementia training certificates (if applicable)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Kentucky-required documentation per 902 KAR 20:081.',
      atRisk: 'Some personnel files are missing required documents. Kentucky requires: KARES background check, Abuse Registry check, annual TB screening, and competency records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by 902 KAR 20:081. CHFS surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 902 KAR 20:081 requirements.',
    },
    stateNotes: [
      'KARES is Kentucky\'s background check portal (906 KAR 1:190)',
      'KBN (Kentucky Board of Nursing) for license verification',
      'Annual TB screening required per 902 KAR 20:205',
      'Dementia training required for staff caring for dementia patients (KRS 216.9375)',
      'All documents must be in place BEFORE patient contact',
    ],
  },

  {
    id: 'KY-B2',
    itemId: 'B2',
    stateCode: 'KY',
    title: 'Home Health Aide Qualifications (902 KAR 20:081)',
    description: 'Kentucky requires specific qualifications for home health aides',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'HHA Requirements',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides meet Kentucky qualification requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'age', label: 'At least 18 years old' },
          { value: 'education', label: 'High school diploma or GED' },
          { value: 'training', label: 'Completed 75-hour training program (or SRNA certification)' },
          { value: 'competency', label: 'Passed competency evaluation' },
          { value: 'cpr', label: 'Current CPR certification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides meet Kentucky qualification requirements per 902 KAR 20:081.',
      atRisk: 'Some home health aides may not meet all qualification requirements. Review credentials immediately.',
      nonCompliant: 'Home health aides are working without proper qualifications. This violates 902 KAR 20:081. Remove unqualified aides from patient care.',
      unknown: 'Verify all home health aide qualifications against CHFS requirements.',
    },
    stateNotes: [
      'Kentucky requires 75 hours initial training (16 clinical hours)',
      'SRNA (State Registered Nurse Aide) certification accepted',
      'Training delegated to Kentucky Community and Technical College System',
      'Competency evaluation must be documented',
      'CPR certification must be current',
    ],
  },

  {
    id: 'KY-B3',
    itemId: 'B3',
    stateCode: 'KY',
    title: 'Professional License Verification (KBN)',
    description: 'Kentucky requires verification of all professional licenses through KBN',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'License Verification',
    agencyName: 'Kentucky Board of Nursing',
    agencyAbbr: 'KBN',
    agencyPhone: '(502) 429-3300',
    agencyUrl: 'https://kbn.ky.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your nurses have current, active Kentucky nursing licenses?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all nurses verified and current' },
          { value: 'most', label: 'Most are verified, some pending' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify licenses through KBN online system BEFORE hire and track renewals?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'hire_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All professional licenses verified through KBN with ongoing tracking.',
      atRisk: 'License verification or tracking is inconsistent. Verify all licenses at kbn.ky.gov and implement renewal tracking.',
      nonCompliant: 'Nurses working without verified Kentucky licenses violates Kentucky Nurse Practice Act. Verify all licenses immediately with KBN at (502) 429-3300.',
      unknown: 'Verify all professional licenses with KBN.',
    },
    stateNotes: [
      'KBN is Kentucky\'s nursing license authority',
      'Free online license verification at kbn.ky.gov',
      'KARES system also validates licensure status automatically',
      'Kentucky participates in Nurse Licensure Compact (NLC)',
    ],
  },

  {
    id: 'KY-B4',
    itemId: 'B4',
    stateCode: 'KY',
    title: 'HIPAA/Confidentiality Agreement',
    description: 'Kentucky requires signed HIPAA and confidentiality agreements',
    severity: 'high',
    regulationCode: '160-164',
    regulationName: 'HIPAA',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers have a signed HIPAA/confidentiality agreement on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have signed agreements' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have signed HIPAA/confidentiality agreements on file.',
      atRisk: 'Some staff are missing signed HIPAA/confidentiality agreements. This creates significant privacy risk.',
      nonCompliant: 'Personnel files are missing required HIPAA/confidentiality agreements.',
      unknown: 'Verify all staff have signed HIPAA/confidentiality agreements on file.',
    },
    stateNotes: [
      'HIPAA Privacy Rule applies per 45 CFR 164',
      'Confidentiality agreement must be signed at hire',
      'Agreement should cover PHI handling and disclosure',
      'Keep signed agreement in personnel file',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'KY-C1',
    itemId: 'C1',
    stateCode: 'KY',
    title: 'KARES Background Check (906 KAR 1:190)',
    description: 'Kentucky requires fingerprint-supported state and national background checks through KARES',
    severity: 'high',
    regulationCode: '906-1-190',
    regulationName: 'KARES',
    agencyName: 'Cabinet for Health and Family Services - KARES',
    agencyAbbr: 'CHFS/KARES',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov/agencies/os/oig/Pages/kares.aspx',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed staff with patient contact, which KARES checks do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'ksp', label: 'Kentucky State Police (KSP) state criminal check' },
          { value: 'fbi', label: 'FBI fingerprint-based national background check' },
          { value: 'nurse_aide', label: 'Kentucky Nurse Aide and Home Health Abuse Registry' },
          { value: 'caregiver', label: 'Kentucky Caregiver Misconduct Registry' },
          { value: 'leie', label: 'Federal LEIE (OIG exclusion list)' },
          { value: 'sex_offender', label: 'Sex Offender Registry check (kspsor.state.ky.us)' },
        ],
        required: true,
        helpText: 'KARES automatically checks abuse registries and LEIE; sex offender registry is a manual check',
      },
      {
        id: 'q2',
        question: 'Do any employees begin working with patients BEFORE their KARES clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - NO patient contact allowed)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 5+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 3+ checks but not all required
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'KARES background check documentation is complete for all staff per 906 KAR 1:190.',
      atRisk: 'Some KARES background checks may be missing. Ensure all required checks are on file.',
      nonCompliant: 'VIOLATION: Staff are working before KARES clearance OR required checks are missing. Per KRS 216.789, NO patient contact is allowed until clearance received. Immediately remove uncleared staff.',
      unknown: 'Audit all staff files for KARES background check documentation.',
    },
    stateNotes: [
      'KARES = Kentucky Applicant Registry and Employment Screening',
      'Fingerprint-supported state (KSP) AND national (FBI) checks required',
      'KARES auto-checks: Nurse Aide Registry, Caregiver Misconduct Registry, Child Abuse Registry, LEIE',
      'Sex Offender Registry is a MANUAL check (kspsor.state.ky.us)',
      'NO patient contact allowed until clearance received',
      'KARES provides rap back (continuous monitoring) for hired employees',
      'Applicant has 30 days from payment to submit fingerprints',
    ],
  },

  {
    id: 'KY-C2',
    itemId: 'C2',
    stateCode: 'KY',
    title: 'Abuse Registry Verification',
    description: 'Must verify staff against Kentucky abuse registries',
    severity: 'high',
    regulationCode: '216.789',
    regulationName: 'Abuse Registry',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(888) 530-1919',
    agencyUrl: 'https://chfs.ky.gov/agencies/os/oig/dcn/Pages/nurseaide.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential hires against Kentucky abuse registries before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, all hires verified through KARES before start date' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / not familiar with this requirement' },
        ],
        required: true,
        helpText: 'KARES automatically checks Nurse Aide Registry, Caregiver Misconduct Registry, and Child Abuse Registry',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse registry checks are conducted for all hires through KARES.',
      atRisk: 'Registry verification is inconsistent. Verify all potential hires through KARES.',
      nonCompliant: 'Abuse registry checks are not being conducted. Persons with substantiated abuse findings cannot work in caregiving roles.',
      unknown: 'Contact CHFS for registry verification procedures.',
    },
    stateNotes: [
      'Kentucky Nurse Aide and Home Health Abuse Registry: (888) 530-1919',
      'Kentucky Caregiver Misconduct Registry: (502) 564-5497',
      'Persons on registry cannot work in caregiving roles',
      'KARES auto-matches against all registries',
    ],
  },

  {
    id: 'KY-C3',
    itemId: 'C3',
    stateCode: 'KY',
    title: 'OIG/LEIE Exclusion List Verification',
    description: 'Federal exclusion list checks required for all staff',
    severity: 'high',
    regulationCode: '42-1001',
    regulationName: 'LEIE',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL employees against the OIG/LEIE exclusion list?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, through KARES at hire AND monthly thereafter' },
          { value: 'hire', label: 'Yes, at hire only (through KARES)' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'KARES auto-checks LEIE; CMS recommends monthly rechecks',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/LEIE exclusion list verification conducted at hire and monthly.',
      atRisk: 'Exclusion list verification at hire only is insufficient. Implement monthly checks (KARES rechecks automatically for hired employees).',
      nonCompliant: 'No OIG/LEIE exclusion list verification conducted. This creates significant Medicare/Medicaid compliance risk.',
      unknown: 'Implement OIG/LEIE exclusion list verification process.',
    },
    stateNotes: [
      'OIG LEIE: exclusions.oig.hhs.gov',
      'KARES auto-checks LEIE at hire and provides rap back monitoring',
      'CMS recommends monthly rechecks',
      'Civil monetary penalties for employing excluded individuals',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'KY-D1',
    itemId: 'D1',
    stateCode: 'KY',
    title: 'TB Screening Requirements (902 KAR 20:205)',
    description: 'Kentucky requires annual TB screening for all staff with patient contact',
    severity: 'high',
    regulationCode: '902-20-205',
    regulationName: 'TB Testing',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff with patient contact have current ANNUAL TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current annual TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'Kentucky requires ANNUAL TB screening per 902 KAR 20:205',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual TB screening documentation is on file for all staff per 902 KAR 20:205.',
      atRisk: 'Some TB screening documentation may be missing or expired. Kentucky requires ANNUAL screening.',
      nonCompliant: 'Staff are working without current TB screening documentation. This violates 902 KAR 20:205. Obtain screening immediately.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'Kentucky requires ANNUAL TB screening per 902 KAR 20:205',
      'Initial screening required before patient contact',
      'Positive TB tests require chest X-ray and medical clearance',
      'Must be 18 years old before clinical portion of training',
    ],
  },

  {
    id: 'KY-D2',
    itemId: 'D2',
    stateCode: 'KY',
    title: 'Infection Control Program (902 KAR 20:081)',
    description: 'Kentucky requires infection control procedures',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Infection Control',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'outbreak', label: 'Outbreak management procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets Kentucky 902 KAR 20:081 requirements.',
      atRisk: 'Infection control program is missing some components. Review 902 KAR 20:081 requirements.',
      nonCompliant: 'Infection control program is inadequate per 902 KAR 20:081. Develop a comprehensive program immediately.',
      unknown: 'Review infection control program against 902 KAR 20:081.',
    },
    stateNotes: [
      'Infection control required per 902 KAR 20:081',
      'Written policies and staff training required',
      'PPE must be available to all direct care staff',
      'Outbreak management procedures required',
    ],
  },

  {
    id: 'KY-D3',
    itemId: 'D3',
    stateCode: 'KY',
    title: 'Hepatitis B Vaccination (OSHA)',
    description: 'OSHA requires Hepatitis B vaccination offer for at-risk employees',
    severity: 'medium',
    regulationCode: '29-1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to all staff with occupational exposure risk?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, offered to all at-risk staff at no cost' },
          { value: 'some', label: 'Offered to some staff' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document vaccination status or signed declination for each employee?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA requirements.',
      atRisk: 'Vaccination is not offered to all at-risk staff. Review OSHA compliance.',
      nonCompliant: 'Failure to offer Hepatitis B vaccination violates OSHA. Implement immediately.',
      unknown: 'Review Hepatitis B vaccination program.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.1030 requires Hepatitis B vaccination offer',
      'Vaccination must be offered at no cost to at-risk employees',
      'Document vaccination status or signed declination',
      'Kentucky OSHA: labor.ky.gov/dows/oshp/',
    ],
  },

  {
    id: 'KY-D4',
    itemId: 'D4',
    stateCode: 'KY',
    title: 'PPE Availability and Training',
    description: 'Kentucky requires PPE availability and training for staff',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'PPE',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is appropriate PPE available to ALL direct care staff?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, PPE is readily available to all staff' },
          { value: 'some', label: 'Available but not consistently' },
          { value: 'no', label: 'PPE is not provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff receive training on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE is available to all staff with proper training.',
      atRisk: 'PPE availability or training is inconsistent. Ensure all staff have access and training.',
      nonCompliant: 'PPE is not provided to staff. This violates OSHA requirements.',
      unknown: 'Verify PPE availability and training for all staff.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.132 requires PPE be provided at no cost',
      'PPE includes gloves, masks, gowns, and eye protection',
      'Staff must be trained on proper PPE use',
      'PPE availability is part of infection control program',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'KY-E1',
    itemId: 'E1',
    stateCode: 'KY',
    title: 'Home Health Aide Initial Training (902 KAR 20:081)',
    description: 'Kentucky requires 75 hours of training for home health aides',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Training',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of initial training do ALL home health aides complete BEFORE patient contact?',
        inputType: 'number',
        required: true,
        helpText: 'Kentucky requires 75 hours (including 16 clinical hours) or SRNA certification',
      },
      {
        id: 'q2',
        question: 'Does initial training include the following required components? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'classroom', label: 'Classroom instruction' },
          { value: 'clinical', label: '16 hours clinical/supervised practical training' },
          { value: 'competency', label: 'Competency evaluation passed' },
          { value: 'skills', label: 'Skills demonstration completed' },
          { value: 'documentation', label: 'Training documentation on file' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 75 },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 40 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 40, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training program meets Kentucky 75-hour requirement per 902 KAR 20:081.',
      atRisk: 'Training program may be incomplete. Kentucky requires 75 hours (16 clinical) initial training.',
      nonCompliant: 'Home health aides are providing care without required 75-hour training. This violates 902 KAR 20:081.',
      unknown: 'Review training program against CHFS requirements.',
    },
    stateNotes: [
      'Kentucky requires 75 hours initial training (same as federal Medicare)',
      'Must include 16 hours clinical/supervised practical training',
      'SRNA (State Registered Nurse Aide) certification accepted',
      'Training delegated to Kentucky Community and Technical College System',
      'Training must be completed BEFORE patient contact',
    ],
  },

  {
    id: 'KY-E2',
    itemId: 'E2',
    stateCode: 'KY',
    title: 'Annual In-Service Training (902 KAR 20:081)',
    description: 'Kentucky requires 12 hours of annual in-service training',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Continuing Education',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of annual in-service training do ALL home health aides complete?',
        inputType: 'number',
        required: true,
        helpText: 'Kentucky requires 12 hours annually',
      },
      {
        id: 'q2',
        question: 'Which required topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'safety', label: 'Patient safety' },
          { value: 'infection', label: 'Infection control' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'abuse', label: 'Abuse recognition and reporting' },
          { value: 'rights', label: 'Patient rights' },
          { value: 'documentation', label: 'Documentation' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 12 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 6 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets Kentucky 12-hour requirement per 902 KAR 20:081.',
      atRisk: 'Some staff may be behind on annual training. Kentucky requires 12 hours annually.',
      nonCompliant: 'Annual in-service training does not meet 12-hour requirement. Implement structured annual training immediately.',
      unknown: 'Verify annual in-service training compliance.',
    },
    stateNotes: [
      'Kentucky requires 12 hours annual in-service (same as federal Medicare)',
      'Training records must be documented in personnel files',
      'Should include abuse reporting refresher annually',
    ],
  },

  {
    id: 'KY-E3',
    itemId: 'E3',
    stateCode: 'KY',
    title: 'Dementia Care Training (KRS 216.9375)',
    description: 'Kentucky requires specific dementia training for staff caring for dementia patients',
    severity: 'high',
    regulationCode: '216.9375',
    regulationName: 'Dementia Training',
    agencyName: 'Kentucky Office of Dementia Services',
    agencyAbbr: 'ODS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you serve patients who exhibit symptoms of Alzheimer\'s disease or other dementia?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How many hours of INITIAL dementia training do staff complete before caring for dementia patients?',
        inputType: 'number',
        helpText: 'KRS 216.9375 requires 6 hours initial dementia training',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'How many hours of ANNUAL dementia training do staff complete?',
        inputType: 'number',
        helpText: 'KRS 216.9375 requires 3 hours annual dementia training',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q2', value: 6 },
        { condition: 'greaterOrEqual', questionId: 'q3', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q2', value: 3 },
        { condition: 'greaterOrEqual', questionId: 'q3', value: 1 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Dementia care training meets KRS 216.9375 requirements (6 hours initial, 3 hours annual).',
      atRisk: 'Dementia training may be incomplete. KRS 216.9375 requires 6 hours initial and 3 hours annual.',
      nonCompliant: 'Staff caring for dementia patients lack required training. KRS 216.9375 mandates 6 hours initial dementia training BEFORE providing services to dementia patients.',
      unknown: 'Review dementia training against KRS 216.9375 requirements.',
    },
    stateNotes: [
      'UNIQUE TO KENTUCKY: KRS 216.9375 requires dementia-specific training',
      'Initial: 6 hours BEFORE providing care to dementia patients',
      'Annual: 3 hours ongoing training',
      'Training curriculum must be approved by Kentucky Office of Dementia Services (910 KAR 4:010)',
      'Certificate of completion required for each aide',
      'If 24+ month lapse in dementia care, must repeat 6-hour initial training',
      'CHFS can impose fines for non-compliance',
    ],
  },

  {
    id: 'KY-E4',
    itemId: 'E4',
    stateCode: 'KY',
    title: 'Mandated Reporter Training (KRS 209.030)',
    description: 'All staff are mandated reporters of vulnerable adult abuse under Kentucky law',
    severity: 'high',
    regulationCode: '209.030',
    regulationName: 'Mandated Reporters',
    agencyName: 'Kentucky Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(800) 752-6200',
    agencyUrl: 'https://chfs.ky.gov/agencies/dcbs/dpp/apb/Pages/default.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained during orientation and annually' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse to the APS hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, APS number (800) 752-6200 is posted and trained' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandated reporter duties per KRS 209.030.',
      atRisk: 'Some staff may lack mandated reporter training. All staff are mandated reporters under Kentucky law.',
      nonCompliant: 'Failure to train staff on mandated reporter duties violates KRS 209.030. Death of the adult does not relieve responsibility for reporting.',
      unknown: 'Verify all staff have received mandated reporter training.',
    },
    stateNotes: [
      'Kentucky Adult Protective Services: (800) 752-6200 or (877) 597-2331',
      'Reports can be made 24/7',
      'All home health workers are mandated reporters under KRS 209.030',
      'Death of the adult does not relieve reporting responsibility',
      'Good faith reports are protected from liability',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'KY-F1',
    itemId: 'F1',
    stateCode: 'KY',
    title: 'Plan of Care (902 KAR 20:081)',
    description: 'Kentucky requires physician-ordered plans of care reviewed every 60 days',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Plan of Care',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all Kentucky-required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Patient diagnosis and medical history' },
          { value: 'services', label: 'Type, nature, and specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Patient-specific goals' },
          { value: 'physician', label: 'Physician/APRN/PA signature' },
          { value: 'review', label: 'Review schedule (every 60 days per 907 KAR 1:030)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are completed before services begin and include all required elements per 902 KAR 20:081.',
      atRisk: 'Plans of care may be incomplete or created after services begin. 902 KAR 20:081 requires written plans before first service.',
      nonCompliant: 'Services are being provided without written plans of care. This violates 902 KAR 20:081. Immediately create plans for all patients.',
      unknown: 'Review plan of care process against 902 KAR 20:081 requirements.',
    },
    stateNotes: [
      'Plan of care must be in place BEFORE services begin',
      'Must be reviewed and signed every 60 days per 907 KAR 1:030',
      'Can be signed by physician, APRN, or PA (per SB 154)',
      'Patient/representative should acknowledge the plan',
    ],
  },

  {
    id: 'KY-F2',
    itemId: 'F2',
    stateCode: 'KY',
    title: 'Clinical Supervision (902 KAR 20:081)',
    description: 'Kentucky requires supervision by physician, RN, or PA',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Supervision',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide clinical supervision of home health aides as required?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, by physician, RN, or PA per regulatory schedule' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does clinical supervision occur?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'At least every 14 days' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_14', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_30', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'less', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical supervision meets 902 KAR 20:081 requirements.',
      atRisk: 'Supervision frequency may not meet requirements. Review schedule to ensure compliance.',
      nonCompliant: 'No clinical supervision program exists. This violates 902 KAR 20:081. Implement supervision immediately.',
      unknown: 'Review supervision requirements per 902 KAR 20:081.',
    },
    stateNotes: [
      'Supervision can be by physician, RN, PA, or APRN (per SB 154)',
      'Supervision recommended at least every 14 days',
      'Must be documented in patient record',
      'Aide must be observed providing services during supervision',
    ],
  },

  {
    id: 'KY-F3',
    itemId: 'F3',
    stateCode: 'KY',
    title: 'Patient Rights (902 KAR 20:081)',
    description: 'Kentucky requires patients be informed of their rights',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Patient Rights',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to ALL patients at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients sign acknowledgment of receiving patient rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided and documented per 902 KAR 20:081.',
      atRisk: 'Patient rights acknowledgment is not documented. Implement signed acknowledgment.',
      nonCompliant: 'Patient rights are not being provided. This violates 902 KAR 20:081.',
      unknown: 'Verify patient rights process meets requirements.',
    },
    stateNotes: [
      'Written patient rights required per 902 KAR 20:081',
      'Must be provided at admission in language patient understands',
      'Include grievance procedures and CHFS complaint hotline',
      'Patient acknowledgment should be documented and signed',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'KY-G1',
    itemId: 'G1',
    stateCode: 'KY',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Record Management',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Agency license (annual renewal)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'workers_comp', label: 'Workers\' compensation' },
          { value: 'nursing_licenses', label: 'Professional nursing licenses (KBN)' },
          { value: 'tb', label: 'Annual TB screenings' },
          { value: 'cpr', label: 'CPR certifications' },
          { value: 'dementia', label: 'Dementia training (annual)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Agency license renews annually',
      'KBN nursing licenses renew per board schedule',
      'TB screening required ANNUALLY per 902 KAR 20:205',
      'Dementia training: 3 hours annually for staff serving dementia patients',
      'KARES provides some automatic renewal notifications',
    ],
  },

  {
    id: 'KY-G2',
    itemId: 'G2',
    stateCode: 'KY',
    title: 'Clinical Documentation (902 KAR 20:081)',
    description: 'Kentucky requires complete and timely clinical documentation',
    severity: 'high',
    regulationCode: '902-20-081',
    regulationName: 'Clinical Records',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which documentation practices does your agency follow? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'consent', label: 'Signed consent for treatment on file' },
          { value: 'assessment', label: 'Initial assessment documented' },
          { value: 'visit_notes', label: 'Visit notes for each service with date and signature' },
          { value: 'signatures', label: 'All entries signed and dated' },
          { value: 'physician_orders', label: 'Physician/APRN/PA orders documented' },
          { value: 'discharge', label: 'Discharge summary completed' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation practices meet Kentucky requirements.',
      atRisk: 'Documentation practices need improvement. Review 902 KAR 20:081 and 907 KAR 1:030 requirements.',
      nonCompliant: 'Documentation practices are inadequate. Implement proper documentation procedures.',
      unknown: 'Review documentation against Kentucky requirements.',
    },
    stateNotes: [
      'Each visit must document date, service provided, and signature',
      'Verbal orders must be documented per 907 KAR 1:030',
      'Records must be kept in organized central file',
      'Electronic signatures permitted per KRS 369.101-369.120',
    ],
  },

  {
    id: 'KY-G3',
    itemId: 'G3',
    stateCode: 'KY',
    title: 'Audit-Ready Documentation',
    description: 'Kentucky requires records be available for CHFS inspection',
    severity: 'medium',
    regulationCode: '902-20-081',
    regulationName: 'Records Availability',
    agencyName: 'Cabinet for Health and Family Services',
    agencyAbbr: 'CHFS',
    agencyPhone: '(502) 564-7963',
    agencyUrl: 'https://chfs.ky.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are your patient and personnel files organized and accessible for CHFS inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any patient\'s or employee\'s complete file within 5 minutes if CHFS arrived for an unannounced inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'mostly' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Files are well-organized and ready for CHFS inspection.',
      atRisk: 'File organization needs improvement. CHFS surveyors expect immediate access to complete files.',
      nonCompliant: 'Disorganized files will result in survey deficiencies. Implement a standardized file structure immediately.',
      unknown: 'Review file organization against CHFS survey requirements.',
    },
    stateNotes: [
      'CHFS conducts unannounced inspections per 902 KAR 20:081',
      'Records must be available for immediate review',
      'Use consistent file structure for all patient and personnel files',
      'Electronic records acceptable per KRS 369.101-369.120',
    ],
  },
];

export default KENTUCKY_QUESTIONS;